import React, { useState, useEffect, useMemo } from 'react';
import { Home, X, MapPin, AlertCircle, AlertTriangle, Check } from 'lucide-react';
import { supabase } from '../../supabaseClient';
import { UF_OPTIONS } from '../../constants/brazilianStates';

/**
 * Modal de cadastro manual de Imóvel.
 *
 * Cria um registro em `imoveis` (fonte_dados='manual', situacao='active') e,
 * opcionalmente, vincula a uma área existente via `area_imoveis`.
 *
 * Sessão frontend-only — não altera schema. As colunas `imoveis.cidade`/`uf`
 * têm default no banco (São Paulo/SP); por isso cidade/uf são OBRIGATÓRIOS e
 * iniciam VAZIOS aqui, para que o humano informe explicitamente e o default
 * silencioso do banco nunca seja aplicado via UI.
 *
 * Falha parcial (imóvel criou, vínculo falhou): aviso amarelo, NÃO deleta o
 * imóvel, NÃO bloqueia — o vínculo é corrigível depois.
 */

// Valores do CHECK constraint imoveis_tipo_uso_atual_check (label PT-BR).
const TIPO_USO_OPTIONS = [
  { value: 'residential', label: 'Residencial' },
  { value: 'commercial', label: 'Comercial' },
  { value: 'mixed', label: 'Misto' },
  { value: 'industrial', label: 'Industrial' },
  { value: 'logistics', label: 'Logístico' },
  { value: 'loteamento', label: 'Loteamento' },
  { value: 'vacant', label: 'Vago / Sem uso' },
  { value: 'rural', label: 'Rural' },
  { value: 'institutional', label: 'Institucional' },
];

const ImovelCreateModal = ({ onClose, onCreated }) => {
  // Identificação
  const [nome, setNome] = useState('');
  const [matricula, setMatricula] = useState('');
  const [cri, setCri] = useState('');
  const [inscricaoMunicipal, setInscricaoMunicipal] = useState('');

  // Localização
  const [endereco, setEndereco] = useState('');
  const [numero, setNumero] = useState('');
  const [complemento, setComplemento] = useState('');
  const [bairro, setBairro] = useState('');
  const [cidade, setCidade] = useState('');   // inicia VAZIO — sem default São Paulo
  const [uf, setUf] = useState('');           // inicia VAZIO — sem default SP
  const [cep, setCep] = useState('');

  // Características
  const [areaTerreno, setAreaTerreno] = useState('');
  const [areaConstruida, setAreaConstruida] = useState('');
  const [tipoUsoAtual, setTipoUsoAtual] = useState('');
  const [valorVenal, setValorVenal] = useState('');
  const [valorReferencia, setValorReferencia] = useState('');
  const [statusJuridico, setStatusJuridico] = useState('');

  // Vínculo
  const [areaId, setAreaId] = useState('');
  const [notas, setNotas] = useState('');

  // Áreas para o dropdown de vínculo
  const [areas, setAreas] = useState([]);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [warning, setWarning] = useState(null);
  const [createdImovel, setCreatedImovel] = useState(null); // preenchido na falha parcial

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error: err } = await supabase
        .from('areas')
        .select('id, nome, localizacao_cidade, localizacao_uf')
        .order('nome');
      if (!cancelled && !err && data) setAreas(data);
    })();
    return () => { cancelled = true; };
  }, []);

  // Pré-preenche cidade/uf ao selecionar área (humano pode editar depois).
  const handleAreaChange = (id) => {
    setAreaId(id);
    const area = areas.find((a) => a.id === id);
    if (area) {
      if (area.localizacao_cidade) setCidade(area.localizacao_cidade);
      if (area.localizacao_uf) setUf(area.localizacao_uf);
    }
  };

  // nome final: explícito, ou auto-gerado de "endereço número".
  const nomeFinal = useMemo(() => {
    if (nome.trim()) return nome.trim();
    if (endereco.trim() && numero.trim()) return `${endereco.trim()} ${numero.trim()}`;
    return '';
  }, [nome, endereco, numero]);

  // Salvar habilitado só com cidade + uf + (nome OU endereço+número).
  const canSave = !!cidade.trim() && !!uf && !!nomeFinal;

  const toNum = (v) => {
    const n = parseFloat(String(v).replace(',', '.'));
    return Number.isFinite(n) ? n : null;
  };

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    setWarning(null);

    try {
      // 1. INSERT imóvel
      const { data: imovel, error: imovelError } = await supabase
        .from('imoveis')
        .insert({
          nome: nomeFinal,
          matricula: matricula.trim() || null,
          cri: cri.trim() || null,
          inscricao_municipal: inscricaoMunicipal.trim() || null,
          endereco: endereco.trim() || null,
          numero: numero.trim() || null,
          complemento: complemento.trim() || null,
          bairro: bairro.trim() || null,
          cidade: cidade.trim(),
          uf,
          cep: cep.trim() || null,
          area_terreno_m2: toNum(areaTerreno),
          area_construida_m2: toNum(areaConstruida),
          tipo_uso_atual: tipoUsoAtual || null,
          valor_venal: toNum(valorVenal),
          valor_referencia: toNum(valorReferencia),
          status_juridico: statusJuridico.trim() || null,
          notas: notas.trim() || null,
          fonte_dados: 'manual',
          situacao: 'active',
        })
        .select('id, nome')
        .single();

      if (imovelError) throw imovelError;

      // 2. Vínculo opcional com área — falha aqui é AVISO, não erro fatal.
      if (areaId) {
        const { error: vinculoError } = await supabase
          .from('area_imoveis')
          .insert({ area_id: areaId, imovel_id: imovel.id });

        if (vinculoError) {
          // Não deletar o imóvel, não bloquear. Mostra aviso e adia o refresh
          // para o clique em "Fechar" (onCreated do pai pode dar reload).
          setCreatedImovel(imovel);
          setWarning(
            'Imóvel criado com sucesso, mas o vínculo com a área falhou. ' +
            'Vincule manualmente na tela da área.'
          );
          setSaving(false);
          return;
        }
      }

      // 3. Sucesso total.
      if (onCreated) onCreated(imovel);
      onClose();
    } catch (err) {
      setError(err.message || 'Erro ao criar imóvel');
      setSaving(false);
    }
  };

  const inputCls =
    'w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500';
  const labelCls = 'block text-xs font-semibold text-slate-700 mb-1';

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[10001] p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">

        {/* Header */}
        <div className="p-5 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
            <Home className="text-green-600" size={20} />
            Novo Imóvel
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X size={22} />
          </button>
        </div>

        <div className="p-5 space-y-5">

          {/* Seção: Identificação */}
          <section className="space-y-3">
            <h3 className="text-xs font-bold uppercase tracking-wide text-slate-500">Identificação</h3>
            <div>
              <label className={labelCls}>
                Nome {nome.trim() ? '' : <span className="text-slate-400 font-normal">(auto-gerado de endereço + número se vazio)</span>}
              </label>
              <input type="text" value={nome} onChange={(e) => setNome(e.target.value)} className={inputCls} placeholder="Ex: Edifício Aurora, ou deixe vazio" autoFocus />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className={labelCls}>Matrícula</label>
                <input type="text" value={matricula} onChange={(e) => setMatricula(e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>CRI</label>
                <input type="text" value={cri} onChange={(e) => setCri(e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Inscrição municipal</label>
                <input type="text" value={inscricaoMunicipal} onChange={(e) => setInscricaoMunicipal(e.target.value)} className={inputCls} />
              </div>
            </div>
          </section>

          {/* Seção: Localização (agrupada visualmente) */}
          <section className="space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-4">
            <h3 className="text-xs font-bold uppercase tracking-wide text-slate-600 flex items-center gap-1.5">
              <MapPin size={13} className="text-green-600" /> Localização
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="sm:col-span-2">
                <label className={labelCls}>Endereço</label>
                <input type="text" value={endereco} onChange={(e) => setEndereco(e.target.value)} className={inputCls} placeholder="Ex: Rua das Acácias" />
              </div>
              <div>
                <label className={labelCls}>Número</label>
                <input type="text" value={numero} onChange={(e) => setNumero(e.target.value)} className={inputCls} placeholder="Ex: 123" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Complemento</label>
                <input type="text" value={complemento} onChange={(e) => setComplemento(e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Bairro</label>
                <input type="text" value={bairro} onChange={(e) => setBairro(e.target.value)} className={inputCls} />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className={labelCls}>Cidade <span className="text-red-500">*</span></label>
                <input type="text" value={cidade} onChange={(e) => setCidade(e.target.value)} className={inputCls} placeholder="Ex: São Paulo" />
              </div>
              <div>
                <label className={labelCls}>UF <span className="text-red-500">*</span></label>
                <select value={uf} onChange={(e) => setUf(e.target.value)} className={`${inputCls} bg-white cursor-pointer`}>
                  <option value="">Selecione...</option>
                  {UF_OPTIONS.map((u) => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>CEP</label>
                <input type="text" value={cep} onChange={(e) => setCep(e.target.value)} className={inputCls} />
              </div>
            </div>
          </section>

          {/* Seção: Características */}
          <section className="space-y-3">
            <h3 className="text-xs font-bold uppercase tracking-wide text-slate-500">Características</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Área terreno (m²)</label>
                <input type="number" value={areaTerreno} onChange={(e) => setAreaTerreno(e.target.value)} className={inputCls} min="0" step="any" />
              </div>
              <div>
                <label className={labelCls}>Área construída (m²)</label>
                <input type="number" value={areaConstruida} onChange={(e) => setAreaConstruida(e.target.value)} className={inputCls} min="0" step="any" />
              </div>
            </div>
            <div>
              <label className={labelCls}>Tipo de uso atual</label>
              <select value={tipoUsoAtual} onChange={(e) => setTipoUsoAtual(e.target.value)} className={`${inputCls} bg-white cursor-pointer`}>
                <option value="">Selecione...</option>
                {TIPO_USO_OPTIONS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Valor venal (R$)</label>
                <input type="number" value={valorVenal} onChange={(e) => setValorVenal(e.target.value)} className={inputCls} min="0" step="any" />
              </div>
              <div>
                <label className={labelCls}>Valor referência (R$)</label>
                <input type="number" value={valorReferencia} onChange={(e) => setValorReferencia(e.target.value)} className={inputCls} min="0" step="any" />
              </div>
            </div>
            <div>
              <label className={labelCls}>Status jurídico</label>
              <input type="text" value={statusJuridico} onChange={(e) => setStatusJuridico(e.target.value)} className={inputCls} placeholder="Ex: regular, em inventário, com ônus" />
            </div>
          </section>

          {/* Seção: Vínculo */}
          <section className="space-y-3">
            <h3 className="text-xs font-bold uppercase tracking-wide text-slate-500">Vínculo</h3>
            <div>
              <label className={labelCls}>Área (opcional)</label>
              <select value={areaId} onChange={(e) => handleAreaChange(e.target.value)} className={`${inputCls} bg-white cursor-pointer`}>
                <option value="">— Sem vínculo —</option>
                {areas.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.nome}{a.localizacao_cidade ? ` — ${a.localizacao_cidade}/${a.localizacao_uf || '?'}` : ''}
                  </option>
                ))}
              </select>
              <div className="text-[10px] text-slate-500 mt-1">Selecionar uma área pré-preenche cidade/UF (editável).</div>
            </div>
            <div>
              <label className={labelCls}>Notas</label>
              <textarea value={notas} onChange={(e) => setNotas(e.target.value)} rows={2} className={`${inputCls} resize-none`} />
            </div>
          </section>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
              <AlertCircle size={14} className="text-red-600 flex-shrink-0 mt-0.5" />
              <span className="text-sm text-red-800">{error}</span>
            </div>
          )}
          {warning && (
            <div className="p-3 bg-amber-50 border border-amber-300 rounded-lg flex items-start gap-2">
              <AlertTriangle size={14} className="text-amber-600 flex-shrink-0 mt-0.5" />
              <span className="text-sm text-amber-900">{warning}</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-200 bg-slate-50 flex justify-end gap-2 sticky bottom-0">
          {warning ? (
            <button
              onClick={() => { if (onCreated) onCreated(createdImovel); else onClose(); }}
              className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              Fechar
            </button>
          ) : (
            <>
              <button onClick={onClose} disabled={saving} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-200 rounded-lg">
                Cancelar
              </button>
              <button
                onClick={handleSave}
                disabled={!canSave || saving}
                className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
                title={!canSave ? 'Preencha cidade, UF e (nome ou endereço + número)' : undefined}
              >
                <Check size={14} />
                {saving ? 'Criando...' : 'Criar imóvel'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImovelCreateModal;
